import { useEffect, useRef, useState, type ComponentType } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  boardNameOf,
  isErrorCode,
  type EntryStepCode,
  type Lesson,
  type ParticipantRole,
} from "@edu/shared";
import type { VideoCodec } from "livekit-client";
import * as api from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { loadRuntimeConfig, type RuntimeConfig } from "../../lib/runtime-config.ts";
import { scheduleLessonHref } from "../schedule/lesson-link.ts";
import { CALL_BACK_LABEL } from "./call-words.ts";
import { useCallExit } from "./use-call-exit.ts";
import { ENTRY_TIMEOUT_MS } from "./entry-steps.ts";
import { useEntryTracker } from "./entry-tracker.ts";
/* ⚠️ Только тип: импорт значением утащил бы чанк комнаты в стартовый бандл */
import type { ServerClock } from "./room-clock.ts";
import { RoomLoading } from "./RoomLoading.tsx";
import { LessonLinkGate } from "./lesson-link-gate.tsx";
import type { LessonRoomProps } from "./LessonRoom.tsx";

/**
 * Комната грузится отдельным чанком, и это не микрооптимизация.
 * Excalidraw плюс LiveKit это порядка мегабайта после gzip; при 128 кбит/с
 * в режиме белого списка (раздел 3.1) это минуты. Сама страница остаётся в
 * стартовом чанке, поэтому запросы урока и токена уходят параллельно с
 * загрузкой чанка, а не после неё.
 *
 * ⚠️ **Импорт руками, а не `lazy` плюс `Suspense`, и это правка редакции 2
 * проекта.** У `lazy` фабрика вызывается на первом рендере элемента, то есть
 * скачивание чанка начиналось ПОСЛЕ ответов API, вопреки абзацу выше. Плюс
 * `Suspense` не даёт ни срока шага, ни его исхода: отказ `import()` летел бы в
 * ближайший рубеж ошибок, которого здесь нет, и вкладка гасла бы в белый экран.
 * Своим состоянием видно и «идёт», и «сколько», и «не доехало», а это ровно то,
 * что показывает `RoomLoading` и что уезжает в телеметрию входа.
 *
 * ⚠️ `import type` у `LessonRoomProps` обязателен: импорт значением утащил бы
 * весь чанк комнаты в стартовый бандл (правило чанкования, раздел 5.1 CLAUDE.md).
 */
function loadRoom(): Promise<ComponentType<LessonRoomProps>> {
  return import("./LessonRoom.tsx").then((m) => m.LessonRoom);
}

/** Что удалось получить для видео. `null` это урок без SFU, но с доской. */
type Video = {
  token: string;
  serverUrl: string;
  /**
   * Вместимость комнаты, считая себя. Приезжает вместе с пропуском и только
   * оттуда: своего числа у клиента нет (условие 7 рубежа включения,
   * `docs/group-room-design.md`, раздел 17).
   *
   * `null` означает «сервер этого поля не прислал», то есть бандл открыт против
   * API старше этой правки. Разные вещи с «в комнате нет мест», и путать их
   * нельзя: по первому мы не режем список плиток вовсе, потому что резать
   * нечем.
   */
  capacity: number | null;
} | null;

/**
 * Вход в урок.
 *
 * Ключевая развилка здесь одна: токен LiveKit может не приехать (`503
 * livekit_unavailable`), и это НЕ повод не пускать в урок. Четвёртый уровень
 * лестницы деградации (раздел 5.5 CLAUDE.md) обязан работать без
 * медиасервера, а пропуск на доску это отдельный тикет, который про LiveKit
 * ничего не знает. Деградация при этом обязана быть явной: молчаливое
 * ухудшение стоит 10% конверсии, это измерено.
 */
export function LessonPage() {
  const { lessonId = "" } = useParams();
  const [params] = useSearchParams();
  /**
   * Ссылка ученика на одно занятие (`?k=`). Пока ключ не разобран, страница
   * урока не делает ничего своего: разбор и вход живут в `LessonLinkGate`.
   *
   * ⚠️ **Ключ держится ЗДЕСЬ, в памяти, а не в адресе.** Шлюз первым делом, до
   * единого запроса, убирает `k` из адреса: иначе `Referrer-Policy same-origin`
   * положил бы адрес с ключом в Referer каждого запроса со страницы, включая
   * `/v1/*`. После этого ключ живёт только в этом состоянии, в том числе для
   * экрана отказа и повтора. Запоминается он во время рендера, а не эффектом:
   * эффект родителя идёт ПОСЛЕ эффектов шлюза, и адрес к тому времени уже чист.
   */
  const urlKey = params.get("k");
  const [held, setHeld] = useState<{ lessonId: string; key: string } | null>(null);
  if (urlKey !== null && (held?.key !== urlKey || held.lessonId !== lessonId)) {
    setHeld({ lessonId, key: urlKey });
  }
  const linkKey = urlKey ?? (held !== null && held.lessonId === lessonId ? held.key : null);
  if (linkKey !== null) {
    return (
      <LessonLinkGate
        key={`${lessonId}:${linkKey}`}
        lessonId={lessonId}
        linkKey={linkKey}
        onEntered={() => setHeld(null)}
      />
    );
  }
  return <LessonEntry />;
}

function LessonEntry() {
  const { lessonId = "" } = useParams();
  const navigate = useNavigate();
  const { state } = useSession();
  const [params] = useSearchParams();

  const [cfg, setCfg] = useState<RuntimeConfig | null>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  /**
   * Момент сервера вместе с показанием монотонных часов в ту же секунду.
   *
   * ⚠️ **Снимается ОДИН раз, при получении карточки урока, и снимается здесь.**
   * Конец занятия посчитан сервером, а остаток от него считает браузер по часам
   * устройства: домашний ноутбук без синхронизации, спешащий на десять минут,
   * показал бы пустую полосу за десять минут до конца, и противоречия на экране
   * не видно, потому что цифры часов показывают «правильное» по этому же
   * компьютеру.
   *
   * ⚠️ **Ниже по дереву пару снимать нельзя.** Обе ветки комнаты
   * перемонтируются при потере и возврате медиасервера, и свежий замер на
   * середине занятия принял бы получасовой давности момент за только что
   * полученный, то есть сам изобрёл бы получасовое расхождение.
   *
   * ⚠️ **Монотонная метка рядом с моментом нужна затем, чтобы поправку не
   * замораживать.** Разницу считает уже комната, на каждый ход часов, и потому
   * переживает починку часов устройства посреди занятия (разбор у `clockSkewAt`).
   *
   * `null` означает «поправлять нечем»: так отвечает сервер старше этой правки.
   */
  const [serverClock, setServerClock] = useState<ServerClock | null>(null);
  const [video, setVideo] = useState<Video>(null);
  const [noVideoReason, setNoVideoReason] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [Room, setRoom] = useState<ComponentType<LessonRoomProps> | null>(null);
  /**
   * Пришёл раньше времени. Отдельным состоянием, а не строкой в `err`, потому
   * что это не поломка: человек всё сделал правильно и просто пришёл рано, а
   * красная строка про ошибку сказала бы ему, что сломана ссылка.
   */
  const [tooEarly, setTooEarly] = useState<number | null>(null);
  /**
   * Уход из звонка с преподавателями в «Уроки» или на экран конца, а не в
   * расписание (`use-call-exit.ts`). Урок уходит, как уходил.
   */
  const callExit = useCallExit({
    lessonId,
    role: lesson?.call?.role ?? null,
    leaveLesson: () => void navigate(scheduleLessonHref(lessonId, lesson?.scheduledAt ?? null)),
  });
  // Ссылки стабильны (у хука `useMemo` по уроку), поэтому годятся в зависимости
  const { entered: callEntered, gone: callGone } = callExit;

  const forceRelay = params.get("relay") === "1";
  // ?adaptive=0 отключает подбор слоя simulcast по размеру видеоэлемента
  const adaptiveStream = params.get("adaptive") !== "0";
  // ?codec=h264 переключает кодек публикации
  const videoCodec: VideoCodec = params.get("codec") === "h264" ? "h264" : "vp8";
  // ?debug=1 открывает панель метрик сразу, минуя сохранённое состояние кнопки
  const debugInspector = params.get("debug") === "1";

  const signedIn = state.status === "signed";

  /**
   * Учёт шагов входа. Живёт здесь, а показывается в двух местах: сначала
   * страницей вместо серой строки, потом оверлеем внутри комнаты (раздел 4.4
   * проекта). Ссылки `begin` и `mark` стабильны, поэтому годятся в зависимости.
   */
  const entry = useEntryTracker();
  const { begin, mark } = entry;

  /** Скачивание чанка комнаты. Уходит параллельно с проверкой доступа. */
  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    begin("room-chunk");
    void loadRoom().then(
      (component) => {
        if (!alive) return;
        // Обновление функцией: setState принял бы компонент за свой updater
        setRoom(() => component);
        mark("room-chunk", "ok");
      },
      () => {
        if (!alive) return;
        // Своего кода отказа у скачивания чанка нет и быть не может: это не
        // запрос к нашему API, а загрузка модуля браузером
        mark("room-chunk", "fail", "network");
      },
    );
    return () => {
      alive = false;
    };
  }, [signedIn, begin, mark]);

  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    begin("access");

    void (async () => {
      try {
        const [config, l] = await Promise.all([
          loadRuntimeConfig(),
          api.getLesson(lessonId, { timeoutMs: ENTRY_TIMEOUT_MS }),
        ]);
        if (!alive) return;
        setCfg(config);
        setLesson(l);
        // Момент получения ответа, а не момент рендера: пара снимается ровно
        // тогда, когда ответ пришёл, и в этом весь её смысл
        setServerClock(
          l.serverNow === undefined ? null : { at: l.serverNow, perfAt: performance.now() },
        );
        mark("access", "ok");
        callEntered(l);
        if (l.status === "ended") return;

        begin("audio");
        try {
          const t = await api.requestToken(lessonId, { timeoutMs: ENTRY_TIMEOUT_MS });
          if (alive) setVideo({ token: t.token, serverUrl: t.url, capacity: t.capacity ?? null });
        } catch (e) {
          if (!alive) return;
          // Отдельный код, а не общая ошибка: клиент обязан отличать «видео
          // нет, продолжаем на доске» от «сервис сломался»
          // `view_only` это просмотр кабинета админом: видео ему не выдаётся
          // намеренно (12.3 docs/admin-panel-design.md), и «не подключилось»
          // здесь было бы неправдой
          setNoVideoReason(
            e instanceof api.ApiError && e.code === "livekit_unavailable"
              ? "Видеосервер недоступен. Урок идёт на доске, звук и видео не работают."
              : e instanceof api.ApiError && e.code === "view_only"
                ? "В просмотре видно только доску, без видео и звука."
                : "Видео не подключилось. Урок идёт на доске.",
          );
          mark("audio", outcomeOf(e), codeOf(e));
        }
      } catch (e) {
        if (!alive) return;
        mark("access", outcomeOf(e), codeOf(e));
        /**
         * ⚠️ Отказ по времени приходит ОТДЕЛЬНЫМ кодом и разбирается раньше
         * общей ветки. Права отвечают 404 и молчат про причину намеренно, а
         * «занятие ещё не началось» это не секрет: если написать это словами
         * «нет доступа», ученик решит, что ссылка сломалась, и напишет
         * преподавателю ровно в ту минуту, когда тот готовится к занятию.
         */
        if (e instanceof api.ApiError && e.code === "entry_not_open") {
          const startsAt = e.details.startsAt;
          setTooEarly(typeof startsAt === "number" ? startsAt : 0);
          return;
        }
        // Конец звонка, в котором вкладка была: экран конца, а не «не найден»
        if (callGone(e)) return;
        setErr(
          e instanceof api.ApiError && e.status === 404
            ? "Урок не найден или у вас нет к нему доступа"
            : api.isTimeoutError(e)
              ? "Сервер не ответил. Проверьте интернет и попробуйте ещё раз"
              : e instanceof Error
                ? e.message
                : String(e),
        );
      }
    })();

    return () => {
      alive = false;
    };
  }, [lessonId, signedIn, begin, mark, callEntered, callGone]);

  if (state.status === "loading") return <Note text="Загрузка…" />;
  if (state.status === "anon") {
    // Ссылку на урок могли открыть в браузере, где сессии ещё нет. Экран входа
    // покажет корень: возвращаться сюда руками не заставляем
    return (
      <main className="lobby">
        <h1>Урок</h1>
        <p className="muted">Чтобы войти в урок, сначала войдите в приложение.</p>
        <Link className="btn" to="/">
          На главную
        </Link>
      </main>
    );
  }

  if (callExit.screen !== null) return <CallEnd text={callExit.screen} />;

  if (tooEarly !== null) {
    return (
      <main className="lobby">
        <h1>Занятие ещё не началось</h1>
        <p className="muted">
          {tooEarly === 0
            ? "Войти можно за пять минут до начала."
            : `Занятие начнётся ${whenText(tooEarly)}. Войти можно за пять минут до начала.`}
        </p>
        {/*
          Вторая строка обязательна. Без неё правило выглядит непреклонным, и
          ученик, которого преподаватель уже позвал, будет обновлять страницу
          вместо того, чтобы написать ему
        */}
        <p className="muted">
          Если преподаватель уже в комнате, дверь открыта: обновите страницу.
        </p>
        <Link className="btn" to="/schedule">
          К расписанию
        </Link>
      </main>
    );
  }

  if (err) {
    return (
      <main className="lobby">
        <h1>Урок</h1>
        <p className="err">{err}</p>
        <Link className="btn" to="/">
          К списку уроков
        </Link>
      </main>
    );
  }

  /**
   * Экран входа вместо серой строки «Открываем урок…».
   *
   * ⚠️ Он показывает и то, что уже готово: без этого шаг «Загружаем комнату»,
   * едущий минуту, неотличим от поломки, а это ровно то, на что жаловались
   * (2.5 проекта).
   */
  if (!cfg || !lesson) return <RoomLoading view={entry.view} place="page" />;

  if (lesson.status === "ended") {
    return (
      <main className="lobby">
        <h1>{lesson.title}</h1>
        <p className="muted">Урок завершён. Доска доступна только на чтение.</p>
        <div className="lobby__row">
          <Link className="btn" to={`/archive/${lesson.id}`}>
            Открыть архив
          </Link>
          <Link className="btn" to="/">
            К списку
          </Link>
        </div>
      </main>
    );
  }

  if (Room === null) return <RoomLoading view={entry.view} place="page" />;

  // Роль приходит с сервера: она вычислена из того, какой колонке урока равен
  // мой user_id. Клиент её больше не выбирает и не присылает
  const role: ParticipantRole = lesson.myRole ?? "student";

  /**
   * Сеанс комнаты. **Здесь и только здесь отсутствие поля превращается в
   * «сеанса нет»**, и это единственное место, где так делать можно.
   *
   * Довод не «наверное, обойдётся», а построение: поле отсутствует ровно тогда,
   * когда отвечает сервер старше условия 5 рубежа включения, а такой сервер строк
   * сеанса не заводит вовсе (`docs/group-room-design.md`, раздел 17). То есть
   * `undefined` там означает «сеансов не существует», и подстановка `null` не
   * догадка, а тот же факт.
   *
   * ⚠️ Уборщику локальных баз этот довод НЕ подходит и переносить его туда
   * нельзя: он смотрит на список прошлых уроков, среди которых могут быть
   * заведённые новым сервером, и там отсутствие поля означает ровно «не знаю».
   */
  const groupSessionId = lesson.groupSessionId ?? null;

  /**
   * Куда уводит выход из комнаты: расписание с раскрытой карточкой ЭТОГО
   * занятия.
   *
   * Раньше здесь стоял корень, а он переадресует на расписание текущей недели,
   * и человек искал коробку занятия глазами в сетке. Между тем минута выхода с
   * урока это минута трёх работ сразу (отметка о приходе, оплата, домашка), и
   * все три живут в карточке занятия.
   *
   * ⚠️ Момент кладётся слепком урока, а местную дату из него считает уже
   * расписание: правило «всё в зоне СМОТРЯЩЕГО» живёт там, и второй его копии в
   * комнате урока быть не должно. Урок без времени (ручной, из раздела «Уроки»)
   * едет без момента: занятия в сетке у него нет, и открывается обычная неделя.
   */
  const backToSchedule = scheduleLessonHref(lesson.id, lesson.scheduledAt ?? null);

  const callRole = lesson.call?.role ?? null;

  return (
    <Room
      lessonId={lesson.id}
      groupSessionId={groupSessionId}
      /* Предметы урока и пары: только у преподавателя, по ним панель заданий
         выбирает блоки. Разбор у свойств `Whiteboard` */
      lessonSubject={lesson.subject}
      pairSubject={lesson.pairSubject}
      video={video}
      collabUrl={cfg.collabUrl}
      /*
        Подпись над курсором. ⚠️ Не `displayName`: имя аккаунта приезжает из
        профиля провайдера входа, где человек записан ФИО (просьба преподавателя
        23.08.2026, раздел 4 docs/room-feedback-fixes.md).

        ⚠️ Считает её СЕРВЕР и отдаёт полем `shownAs`: у занятия группы первая
        ступень правила это имя внутри группы, которого у клиента нет и быть не
        должно. `boardNameOf` остаётся запасным вариантом ровно на один случай:
        сервер старше условия 4 рубежа включения, поля не прислал.
      */
      boardName={lesson.shownAs ?? boardNameOf(state.me)}
      /* Название урока в шапку комнаты. Пустая строка это отсутствие имени, а
         не имя из пробелов: у ручного урока его может не быть вовсе */
      title={lesson.title.trim() === "" ? null : lesson.title}
      /*
        Занятие по расписанию: из него шапка рисует преподавателю часы и полосу
        остатка (просьба преподавателя 29.09.2026).

        ⚠️ Отсутствие поля здесь превращается в `null` по тому же доводу, что у
        сеанса выше, и довод этот в обе стороны безопасен: `null` и
        `undefined` означают тут ОДНО И ТО ЖЕ действие, полосы не будет. Старый
        сервер конца не присылает, ручной урок его не имеет, и в обоих случаях
        выдуманный конец хуже отсутствующего.

        ⚠️ Конец НЕ складывается здесь из `scheduledAt` и длительности: её
        источников четыре (правка занятия, слот, серия, разовый урок), и
        разрешает их сервер одним выражением (`occurrenceEndsAtSql`). Копия
        этого порядка на клиенте разошлась бы с ним в день, когда у
        длительности появится пятый источник.
      */
      scheduledAt={lesson.scheduledAt ?? null}
      scheduledEndsAt={lesson.scheduledEndsAt ?? null}
      serverClock={serverClock}
      role={role}
      sampleIntervalMs={cfg.telemetry.sampleIntervalMs}
      telemetryEnabled={cfg.telemetry.enabled}
      noVideoReason={noVideoReason}
      forceRelay={forceRelay}
      adaptiveStream={adaptiveStream}
      videoCodec={videoCodec}
      debugInspector={debugInspector}
      entry={entry}
      onLeave={callExit.exit.leave}
      call={callRole}
      /*
        Завершение занятия заведено здесь, а не в комнате: уводит отсюда
        маршрутизатор, и комната про маршруты ничего не знает. Снимок доски
        снимает сервер в той же ручке, поэтому здесь только вызов и уход.

        ⚠️ Отказ НЕ гасится: он всплывает в кнопку, и она говорит «попробуйте
        ещё раз». Проглотить его значило бы показать, что занятие закрыто,
        когда оно осталось живым, и человек ушёл бы с уроком в архиве через
        пятнадцать минут вместо мгновенного.
      */
      onEnd={async () => {
        // Звонок уходит на «Уроки», и 404 у него это успех (`use-call-exit.ts`)
        if (callRole !== null) return callExit.end();
        await api.endLesson(lesson.id);
        /*
          ⚠️ ЗАМЕНОЙ, в отличие от «Выйти» строкой выше, и разница не
          стилистическая: занятие закончилось, возвращаться в него нечем.
          «Назад» после push показывал бы экран «Урок завершён» с двумя
          ссылками, то есть тупик там, где человек ждёт предыдущий экран.
          У «Выйти» наоборот, там push обязателен: выйти можно случайно, а тем
          же обработчиком комната уходит и при обрыве связи (`onDisconnected` в
          `LessonRoom`), и «назад» это самый дешёвый способ вернуться в живой
          урок.
        */
        void navigate(backToSchedule, { replace: true });
      }}
    />
  );
}

/**
 * Исход шага: `timeout` это отсутствие ответа, `fail` это ответ «нет».
 *
 * Разделены не для красоты словаря: у первого чинится сеть, у второго доступ, и
 * на медленном канале `timeout` означает не поломку, а слишком низкий порог
 * ожидания у нас. Смешай их, и в логе не отличить одно от другого никогда.
 */
function outcomeOf(e: unknown): "fail" | "timeout" {
  return api.isTimeoutError(e) ? "timeout" : "fail";
}

/**
 * Код отказа для телеметрии входа.
 *
 * ⚠️ Через `isErrorCode`, а не как есть. `ApiError.code` бывает синтетическим
 * (`http_500` собирается тут же, когда тело не наше), а схема события принимает
 * только словарные коды и отвергает всё событие целиком. То есть один
 * непредусмотренный отказ стоил бы всей строки в логе, причём молча.
 */
function codeOf(e: unknown): EntryStepCode {
  if (api.isTimeoutError(e) || !(e instanceof api.ApiError)) return "network";
  return isErrorCode(e.code) ? e.code : "network";
}

/**
 * Момент начала словами, в зоне БРАУЗЕРА.
 *
 * Именно браузера, а не подтверждённой зоны профиля: ученик мог вовсе не
 * заходить в кабинет и зону не подтверждать, а часы у него на экране идут по
 * его собственной зоне. Показать «в 16:00» по чужим часам значит отправить
 * человека ждать не тот час.
 */
function whenText(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const time = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(d);
  if (sameDay) return `сегодня в ${time}`;
  const day = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" }).format(d);
  return `${day} в ${time}`;
}

/** Экран конца звонка (9.5 проекта). Фокус на заголовок, как у `CallLinkPage.tsx`. */
function CallEnd({ text }: { text: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <main className="lobby">
      <h1 ref={heading} tabIndex={-1}>
        {text}
      </h1>
      <Link className="btn" to="/lessons">
        {CALL_BACK_LABEL}
      </Link>
    </main>
  );
}

function Note({ text }: { text: string }) {
  return (
    <main className="lobby">
      <p className="muted">{text}</p>
    </main>
  );
}
